/**
 * Aviso de que o acesso de alguém mudou.
 *
 * ## O problema que isto resolve
 *
 * A liberação acontece no servidor, minutos depois do pedido, e a pessoa está
 * com o portfólio aberto. O sino descobria a novidade — ele consulta sozinho —
 * mas os cartões atrás continuavam dizendo "Sem acesso", porque a lista tinha
 * sido carregada uma vez e nunca mais. O aviso e a tela discordavam, e só um
 * F5 resolvia.
 *
 * Quem descobre é o sino; quem precisa saber é o portfólio. Passar a função de
 * mão em mão acoplaria dois componentes que não têm relação — o mesmo motivo
 * pelo qual o `Toast` existe assim.
 *
 * ## Por que não recarregar o catálogo em laço
 *
 * O portfólio não muda sozinho: ele muda quando UMA coisa acontece, e o sino
 * já pergunta por ela. Consultar o catálogo a cada dez segundos gastaria vinte
 * itens e uma decisão de acesso por consulta para, quase sempre, receber o
 * mesmo de antes.
 */

const listeners = new Set();

/**
 * Avisa que o acesso mudou. Chamado pelo sino quando chega notificação de
 * liberação, recusa ou revogação — e pela tela de solicitações.
 */
export function emitAccessChanged() {
  for (const listener of listeners) listener();
}

/** Inscreve. Devolve a função que cancela, para o `useEffect` limpar. */
export function onAccessChanged(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ── espera ativa ────────────────────────────────────────────── */

const watchers = new Set();

/**
 * Avisa que existe um pedido em andamento.
 *
 * Disparado quando alguém solicita acesso. O sino consulta a cada 30 segundos
 * no dia a dia — o que somado à espera da aprovação deixaria a pessoa até um
 * minuto olhando para "Sem acesso" depois de já estar liberada.
 *
 * Enquanto há pedido em andamento, ele consulta a cada 5 segundos. É a única
 * janela em que a pessoa está de fato esperando algo, e é curta.
 */
export function emitPendingWatch() {
  for (const w of watchers) w();
}

export function onPendingWatch(fn) {
  watchers.add(fn);
  return () => watchers.delete(fn);
}

/** Os tipos de notificação que mexem no que a pessoa pode abrir. */
export const ACCESS_KINDS = Object.freeze([
  'REQUEST_APPROVED',
  'REQUEST_REJECTED',
  'ACCESS_REVOKED',
]);

export default {
  emitAccessChanged,
  onAccessChanged,
  emitPendingWatch,
  onPendingWatch,
  ACCESS_KINDS,
};
